import { signOut } from 'next-auth/react';
import { useCallback } from 'react';

import { LOCAL_STORAGE_KEYS as HOME_SELECTOR_KEYS } from '../components/dynamicPages/CreatePage/CreateFlow/useHomeSelectorsState';
import { HOME_LOCAL_STORAGE_KEYS } from '../components/dynamicPages/HomePage/homeLocationStorage';
import backendService from '../services/backendService';
import { clearPostAuthRedirect } from '../utils/postAuthRedirect';

async function attemptClearCache() {
  try {
    await backendService.instance.users.clearCache();
  } catch (error) {}
}

/**
 * The home page's selector keys aren't scoped to a user, so whatever the last
 * person picked on this browser is read back for the next one who signs in —
 * ahead of their profile and ahead of any country check. Subject is the one
 * that shows, because the option list differs by country and an inherited
 * value may not be in the new teacher's dropdown at all; grade and state leak
 * the same way but happen to be invisible.
 *
 * Clearing on sign-out is the cheap half of the fix. Scoping the keys per user
 * is the complete one, but it would reset every existing teacher's stored pick.
 *
 * Deliberately NOT cleared: `internationalEnglishMigration`. That one isn't a
 * selection, it's a per-device "the CURI-301 one-off already ran" marker, and
 * clearing it re-arms the migration. A teacher who picked their native
 * language in onboarding has it stored as ELA and never sees the
 * international-school modal (that only fires on English/Literacy), so
 * `organization.isInternational` stays null for them — which makes this flag
 * the only thing standing between their choice and being silently switched to
 * English on the next sign-in.
 */
function clearHomeSelectors() {
  if (typeof window === 'undefined') return;
  const keys: string[] = [
    HOME_SELECTOR_KEYS.grade,
    HOME_SELECTOR_KEYS.subject,
    ...Object.values(HOME_LOCAL_STORAGE_KEYS),
  ];
  for (const key of keys) {
    try {
      localStorage.removeItem(key);
    } catch {}
  }
}

function useSignOut() {
  // There's no hosted IdP sign-out to round-trip through — clear the Curipod
  // session and land back on our own origin.
  const signOutAuto = useCallback(async (redirectUrl?: string) => {
    await attemptClearCache(); // Even if this fails, user should be signed out
    clearPostAuthRedirect();
    clearHomeSelectors();
    await signOut({ callbackUrl: redirectUrl || window.location.origin });
  }, []);

  return {
    signOutAuto,
  };
}

export default useSignOut;
